import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Wallet, TrendingUp, TrendingDown, Settings, UserPlus, Coins,
  CreditCard, Receipt, ClipboardList, Plus, Trash2, FileDown, Search,
  CheckCircle2, AlertTriangle, XCircle, DollarSign, Users,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";

const MOIS = ["octobre", "novembre", "decembre", "janvier", "fevrier", "mars", "avril", "mai", "juin", "juillet"];
const MOIS_LBL = { octobre: "Octobre", novembre: "Novembre", decembre: "Décembre", janvier: "Janvier", fevrier: "Février", mars: "Mars", avril: "Avril", mai: "Mai", juin: "Juin", juillet: "Juillet" };

const CATEGORIES = [
  { id: "salaires", label: "Salaires" },
  { id: "fournitures", label: "Fournitures scolaires" },
  { id: "eau", label: "Eau" },
  { id: "electricite", label: "Électricité" },
  { id: "internet", label: "Internet" },
  { id: "entretien", label: "Entretien" },
  { id: "securite", label: "Sécurité" },
  { id: "transport", label: "Transport" },
  { id: "cantine", label: "Cantine" },
  { id: "informatique", label: "Matériel informatique" },
  { id: "divers", label: "Divers" },
];

const MODES_PAIEMENT = [
  { id: "espece", label: "Espèces" },
  { id: "wave", label: "Wave" },
  { id: "orange_money", label: "Orange Money" },
  { id: "free_money", label: "Free Money" },
  { id: "carte", label: "Carte bancaire" },
  { id: "virement", label: "Virement" },
];

const TYPES_PAIEMENT = [
  { id: "inscription", label: "Frais d'inscription" },
  { id: "reinscription", label: "Réinscription" },
  { id: "mensualite", label: "Mensualité" },
  { id: "cotisation", label: "Cotisation" },
  { id: "unique", label: "Paiement unique annuel" },
  { id: "autre", label: "Autre" },
];

const fmt = (n) => (Number(n) || 0).toLocaleString("fr-FR");

const VALID = ["dashboard", "config", "inscriptions", "mensualites", "cotisations", "paiements", "depenses"];

export default function Finance() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const initial = VALID.includes(urlTab) ? urlTab : "dashboard";
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    if (urlTab && VALID.includes(urlTab) && urlTab !== tab) setTab(urlTab);
    // eslint-disable-next-line
  }, [urlTab]);
  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const schoolId = user.school_id;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Wallet className="w-7 h-7 text-[#1F7A63]" /> Finance
        </h1>
        <p className="text-sm text-[#5C736A]">Gestion financière complète : recettes, dépenses, mensualités, cotisations.</p>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="hidden">
          <TabsTrigger value="dashboard" />
          <TabsTrigger value="config" />
          <TabsTrigger value="inscriptions" />
          <TabsTrigger value="mensualites" />
          <TabsTrigger value="cotisations" />
          <TabsTrigger value="paiements" />
          <TabsTrigger value="depenses" />
        </TabsList>

        <TabsContent value="dashboard" className="mt-5"><Dashboard schoolId={schoolId} /></TabsContent>
        <TabsContent value="config" className="mt-5"><Config schoolId={schoolId} /></TabsContent>
        <TabsContent value="inscriptions" className="mt-5"><Inscriptions schoolId={schoolId} /></TabsContent>
        <TabsContent value="mensualites" className="mt-5"><Mensualites schoolId={schoolId} /></TabsContent>
        <TabsContent value="cotisations" className="mt-5"><Cotisations schoolId={schoolId} /></TabsContent>
        <TabsContent value="paiements" className="mt-5"><Paiements schoolId={schoolId} /></TabsContent>
        <TabsContent value="depenses" className="mt-5"><Depenses schoolId={schoolId} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =============== 1. DASHBOARD =============== */
function Dashboard({ schoolId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/finance/dashboard", { params: { school_id: schoolId } })
      .then(({ data }) => setD(data))
      .catch((e) => toast.error(formatError(e)));
  }, [schoolId]);
  if (!d) return <div className="skel h-40" />;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label={`Recettes ce mois (${d.current_month || ""})`} value={fmt(d.total_recettes_mois)} suffix={d.devise} icon={TrendingUp} tone="green" />
        <Kpi label={`Dépenses ce mois`} value={fmt(d.total_depenses_mois)} suffix={d.devise} icon={TrendingDown} tone="red" />
        <Kpi label="Solde caisse" value={fmt(d.solde)} suffix={d.devise} icon={Wallet} tone={d.solde >= 0 ? "green" : "red"} />
        <Kpi label="Cotisations collectées" value={fmt(d.cotisations_total)} suffix={d.devise} icon={Coins} tone="yellow" />
        <Kpi label="Élèves à jour" value={d.eleves_a_jour} icon={CheckCircle2} tone="green" />
        <Kpi label="Élèves en retard" value={d.eleves_en_retard} icon={AlertTriangle} tone="yellow" />
        <Kpi label="Montant impayés" value={fmt(d.impayes)} suffix={d.devise} icon={XCircle} tone="red" />
        <Kpi label="Devise" value={d.devise} icon={DollarSign} tone="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Recettes des 6 derniers mois">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.recettes_par_mois}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v) + " " + d.devise} />
              <Bar dataKey="recettes" fill="#1F7A63" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Dépenses par catégorie">
          {d.depenses_par_categorie.length === 0 ? <Empty text="Aucune dépense enregistrée." /> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={d.depenses_par_categorie} dataKey="total" nameKey="categorie" outerRadius={80} label>
                  {d.depenses_par_categorie.map((_, i) => (
                    <Cell key={i} fill={["#1F7A63", "#E6B800", "#2563EB", "#D93838", "#8B5CF6", "#F59E0B", "#10B981", "#EC4899", "#6366F1", "#14B8A6", "#F97316"][i % 11]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmt(v) + " " + d.devise} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <ChartCard title="Taux de paiement par classe">
        {d.taux_par_classe.length === 0 ? <Empty text="Aucune donnée." /> : (
          <div className="space-y-2">
            {d.taux_par_classe.map((c) => (
              <div key={c.classe} className="flex items-center gap-3">
                <div className="w-24 text-sm font-semibold shrink-0">{c.classe}</div>
                <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden relative">
                  <div className="h-full bg-[#1F7A63] transition-all"
                       style={{ width: `${c.taux}%` }} />
                  <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold">
                    {c.taux}% ({c.paid}/{c.total})
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ChartCard>
    </div>
  );
}

/* =============== 2. CONFIG =============== */
function Config({ schoolId }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/finance/config", { params: { school_id: schoolId } })
      .then(({ data }) => setCfg(data || { school_id: schoolId, mode: "mensualites", devise: "FCFA", frais_inscription: 0, frais_reinscription: 0, montant_mensualite: 0, frais_annuel_unique: 0, mois_actifs: MOIS }))
      .catch((e) => toast.error(formatError(e)));
  }, [schoolId]);
  if (!cfg) return <div className="skel h-40" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/finance/config", cfg);
      toast.success("Configuration enregistrée");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const toggleMois = (m) => {
    setCfg({ ...cfg, mois_actifs: cfg.mois_actifs.includes(m)
      ? cfg.mois_actifs.filter((x) => x !== m)
      : [...cfg.mois_actifs, m] });
  };

  return (
    <div className="space-y-5">
      <div className="ek-card p-5">
        <h3 className="font-semibold mb-3" style={{ fontFamily: "Outfit" }}>Mode de fonctionnement</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { id: "mensualites", label: "Mensualités", desc: "Inscription + versement chaque mois" },
            { id: "unique", label: "Paiement unique", desc: "Frais annuels payés en une fois" },
            { id: "mixte", label: "Mixte", desc: "Inscription + mensualités + cotisations" },
          ].map((m) => (
            <button
              key={m.id}
              data-testid={`mode-${m.id}`}
              onClick={() => setCfg({ ...cfg, mode: m.id })}
              className={`text-left ek-card p-4 border-2 transition-all ${cfg.mode === m.id ? "border-[#1F7A63] bg-[#E8F2EF]" : "border-transparent"}`}
            >
              <div className="font-semibold text-sm">{m.label}</div>
              <div className="text-xs text-[#5C736A] mt-1">{m.desc}</div>
              {cfg.mode === m.id && <div className="text-xs mt-2 inline-block bg-[#E6B800] px-2 py-0.5 rounded-full">Actif</div>}
            </button>
          ))}
        </div>
      </div>

      <div className="ek-card p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Devise</Label>
          <Input value={cfg.devise} onChange={(e) => setCfg({ ...cfg, devise: e.target.value })} data-testid="cfg-devise" />
        </div>

        {/* Mode UNIQUE : montre uniquement les frais annuels + inscription */}
        {cfg.mode === "unique" && (
          <>
            <div>
              <Label>Frais d&apos;inscription (opt.)</Label>
              <Input type="number" value={cfg.frais_inscription} onChange={(e) => setCfg({ ...cfg, frais_inscription: Number(e.target.value) })} data-testid="cfg-inscr" />
            </div>
            <div className="md:col-span-2">
              <Label>Paiement unique annuel</Label>
              <Input type="number" value={cfg.frais_annuel_unique} onChange={(e) => setCfg({ ...cfg, frais_annuel_unique: Number(e.target.value) })} data-testid="cfg-annuel" />
              <p className="text-xs text-[#5C736A] mt-1">Montant total à régler en une seule fois pour toute l&apos;année.</p>
            </div>
          </>
        )}

        {/* Mode MENSUALITÉS : montre inscription + montant mensualité */}
        {cfg.mode === "mensualites" && (
          <>
            <div>
              <Label>Frais d&apos;inscription</Label>
              <Input type="number" value={cfg.frais_inscription} onChange={(e) => setCfg({ ...cfg, frais_inscription: Number(e.target.value) })} data-testid="cfg-inscr" />
            </div>
            <div>
              <Label>Frais de réinscription</Label>
              <Input type="number" value={cfg.frais_reinscription} onChange={(e) => setCfg({ ...cfg, frais_reinscription: Number(e.target.value) })} />
            </div>
            <div className="md:col-span-2">
              <Label>Montant d&apos;une mensualité</Label>
              <Input type="number" value={cfg.montant_mensualite} onChange={(e) => setCfg({ ...cfg, montant_mensualite: Number(e.target.value) })} data-testid="cfg-mens" />
            </div>
          </>
        )}

        {/* Mode MIXTE : tout affiché */}
        {cfg.mode === "mixte" && (
          <>
            <div>
              <Label>Frais d&apos;inscription</Label>
              <Input type="number" value={cfg.frais_inscription} onChange={(e) => setCfg({ ...cfg, frais_inscription: Number(e.target.value) })} data-testid="cfg-inscr" />
            </div>
            <div>
              <Label>Frais de réinscription</Label>
              <Input type="number" value={cfg.frais_reinscription} onChange={(e) => setCfg({ ...cfg, frais_reinscription: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Montant d&apos;une mensualité</Label>
              <Input type="number" value={cfg.montant_mensualite} onChange={(e) => setCfg({ ...cfg, montant_mensualite: Number(e.target.value) })} data-testid="cfg-mens" />
            </div>
            <div>
              <Label>Paiement unique annuel (opt.)</Label>
              <Input type="number" value={cfg.frais_annuel_unique} onChange={(e) => setCfg({ ...cfg, frais_annuel_unique: Number(e.target.value) })} />
            </div>
          </>
        )}
      </div>

      {/* Mois actifs : uniquement en mensualités et mixte */}
      {cfg.mode !== "unique" && (
        <div className="ek-card p-5">
          <h3 className="font-semibold mb-3" style={{ fontFamily: "Outfit" }}>Mois de l&apos;année scolaire</h3>
          <div className="flex flex-wrap gap-2">
            {MOIS.map((m) => (
              <button
                key={m}
                onClick={() => toggleMois(m)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  cfg.mois_actifs.includes(m)
                    ? "bg-[#1F7A63] text-white border-[#1F7A63]"
                    : "bg-white text-[#5C736A] border-slate-200"
                }`}
              >{MOIS_LBL[m]}</button>
            ))}
          </div>
        </div>
      )}

      <div className="sticky bottom-0 pt-4 bg-gradient-to-t from-slate-50 via-slate-50">
        <Button onClick={save} disabled={saving} className="bg-[#1F7A63] hover:bg-[#155744] text-white w-full sm:w-auto" data-testid="cfg-save">
          {saving ? "Enregistrement…" : "Enregistrer la configuration"}
        </Button>
      </div>
    </div>
  );
}

/* =============== 3. INSCRIPTIONS =============== */
function Inscriptions({ schoolId }) {
  const [list, setList] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", montant: 0, exonere: false, boursier: false, date: new Date().toISOString().slice(0, 10), note: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/finance/inscriptions", { params: { school_id: schoolId } });
      setList(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => {
    load();
    api.get("/students").then(({ data }) => setStudents([...data].sort((a, b) =>
      (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
      || (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" })
    ))).catch(() => {});
    // eslint-disable-next-line
  }, [schoolId]);

  const submit = async () => {
    try {
      await api.post("/finance/inscriptions", form);
      toast.success("Inscription enregistrée");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <Button className="bg-[#1F7A63] hover:bg-[#155744] text-white" onClick={() => setOpen(true)} data-testid="inscr-new">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle inscription
          </Button>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle inscription</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Élève</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>
                    {students.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} — {s.matricule}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Montant (FCFA)</Label>
                <Input type="number" value={form.montant} onChange={(e) => setForm({ ...form, montant: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.exonere} onChange={(e) => setForm({ ...form, exonere: e.target.checked })} /> Exonéré</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.boursier} onChange={(e) => setForm({ ...form, boursier: e.target.checked })} /> Boursier</label>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#155744] text-white">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="ek-card p-0 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-[#5C736A]">
            <tr>
              <th className="text-left px-3 py-2">Élève</th>
              <th className="text-right px-3 py-2">Montant</th>
              <th className="text-center px-3 py-2">Statut</th>
              <th className="text-center px-3 py-2">Date</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => {
              const st = students.find((s) => s.id === r.student_id);
              return (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">{st ? `${st.prenom} ${st.nom}` : r.student_id.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmt(r.montant)}</td>
                  <td className="px-3 py-2 text-center">
                    {r.exonere && <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-full text-xs">Exonéré</span>}
                    {r.boursier && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs ml-1">Boursier</span>}
                    {!r.exonere && !r.boursier && <span className="text-[#5C736A]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-[#5C736A]">{r.date}</td>
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr><td colSpan={4} className="text-center py-10 text-[#5C736A] text-sm">Aucune inscription enregistrée.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =============== 4. MENSUALITÉS =============== */
function Mensualites({ schoolId: _schoolId }) {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [status, setStatus] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ student_id: "", mois: "", montant: 0, mode: "espece" });

  useEffect(() => {
    api.get("/classes").then(({ data }) => { setClasses(data); if (data[0]) setClassId(data[0].id); });
  }, []);

  const load = async () => {
    if (!classId) return;
    try {
      const { data } = await api.get("/finance/mensualites/status", { params: { class_id: classId } });
      setStatus(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classId]);

  const submit = async () => {
    try {
      await api.post("/payments", {
        student_id: payForm.student_id, type: "mensualite",
        montant: payForm.montant, mois: payForm.mois, mode: payForm.mode,
        date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Paiement enregistré");
      setPayOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="ek-card p-3 flex gap-2 items-center">
        <Label className="mb-0 text-sm">Classe :</Label>
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Classe" /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
        </Select>
        {status && (
          <div className="ml-auto text-xs text-[#5C736A]">
            Attendu : <b>{fmt(status.config.montant_mensualite)}</b> FCFA/mois · <b>{status.due_months.length}</b> mois échus
          </div>
        )}
      </div>

      {!status ? <div className="skel h-40" /> : (
        <div className="ek-card p-0 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[#5C736A]">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Élève</th>
                <th className="text-right px-3 py-2">Attendu</th>
                <th className="text-right px-3 py-2">Payé</th>
                <th className="text-right px-3 py-2">Reste</th>
                <th className="text-center px-3 py-2">Statut</th>
                <th className="text-center px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {status.students.map((r) => (
                <tr key={r.student.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 sticky left-0 bg-inherit whitespace-nowrap">{r.student.prenom} <span className="font-semibold">{r.student.nom}</span></td>
                  <td className="px-3 py-2 text-right">{fmt(r.expected)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.paid)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-red-600">{fmt(r.reste)}</td>
                  <td className="px-3 py-2 text-center">
                    {r.statut === "A_JOUR" && <span className="bg-[#E8F2EF] text-[#1F7A63] px-2 py-0.5 rounded-full text-xs font-semibold">À jour</span>}
                    {r.statut === "PARTIEL" && <span className="bg-[#FCF8E6] text-[#b38f00] px-2 py-0.5 rounded-full text-xs font-semibold">Partiel</span>}
                    {r.statut === "IMPAYE" && <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-full text-xs font-semibold">Impayé</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Button size="sm" className="bg-[#E6B800] hover:bg-[#b38f00] text-[#1A2E28]"
                            onClick={() => { setPayForm({ student_id: r.student.id, mois: status.due_months[status.due_months.length - 1] || "octobre", montant: status.config.montant_mensualite, mode: "espece" }); setPayOpen(true); }}>
                      <Plus className="w-3 h-3 mr-1" /> Payer
                    </Button>
                  </td>
                </tr>
              ))}
              {status.students.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#5C736A] text-sm">Aucun élève dans cette classe.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Enregistrer un paiement de mensualité</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mois</Label>
              <Select value={payForm.mois} onValueChange={(v) => setPayForm({ ...payForm, mois: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MOIS.map((m) => <SelectItem key={m} value={m}>{MOIS_LBL[m]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant</Label>
              <Input type="number" value={payForm.montant} onChange={(e) => setPayForm({ ...payForm, montant: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Mode de paiement</Label>
              <Select value={payForm.mode} onValueChange={(v) => setPayForm({ ...payForm, mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MODES_PAIEMENT.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Annuler</Button>
            <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#155744] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* =============== 5. COTISATIONS =============== */
function Cotisations({ schoolId }) {
  const [list, setList] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nom: "", description: "", montant: 0, date_limite: "", classes: [] });

  const load = async () => {
    try { setList((await api.get("/finance/cotisations", { params: { school_id: schoolId } })).data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => {
    load();
    api.get("/classes").then(({ data }) => setClasses(data));
    // eslint-disable-next-line
  }, [schoolId]);

  const submit = async () => {
    try {
      await api.post("/finance/cotisations", { ...form, school_id: schoolId });
      toast.success("Cotisation créée");
      setForm({ nom: "", description: "", montant: 0, date_limite: "", classes: [] });
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Supprimer cette cotisation ?")) return;
    try { await api.delete(`/finance/cotisations/${id}`); load(); toast.success("Supprimée"); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <Button className="bg-[#1F7A63] hover:bg-[#155744] text-white" onClick={() => setOpen(true)} data-testid="cot-new">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle cotisation
          </Button>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle cotisation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Cotisation examen, tenue, cantine…" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Montant (FCFA)</Label><Input type="number" value={form.montant} onChange={(e) => setForm({ ...form, montant: Number(e.target.value) })} /></div>
              <div><Label>Date limite</Label><Input type="date" value={form.date_limite} onChange={(e) => setForm({ ...form, date_limite: e.target.value })} /></div>
              <div>
                <Label>Classes concernées (vide = toutes)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {classes.map((c) => (
                    <button key={c.id} type="button" onClick={() => setForm({ ...form, classes: form.classes.includes(c.id) ? form.classes.filter((x) => x !== c.id) : [...form.classes, c.id] })}
                            className={`px-2 py-1 rounded-full text-xs border ${form.classes.includes(c.id) ? "bg-[#1F7A63] text-white border-[#1F7A63]" : "bg-white text-[#5C736A] border-slate-200"}`}>
                      {c.nom}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#155744] text-white">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((c) => (
          <div key={c.id} className="ek-card p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0">
                <div className="font-semibold truncate" style={{ fontFamily: "Outfit" }}>{c.nom}</div>
                {c.date_limite && <div className="text-xs text-[#5C736A]">Limite : {c.date_limite}</div>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {c.description && <p className="text-xs text-[#5C736A] mb-2">{c.description}</p>}
            <div className="text-2xl font-bold text-[#1F7A63]" style={{ fontFamily: "Outfit" }}>{fmt(c.montant)} <span className="text-sm text-[#5C736A]">FCFA</span></div>
            <div className="text-xs text-[#5C736A] mt-1">
              Classes : {c.classes.length === 0 ? "toutes" : c.classes.length + " sélectionnée(s)"}
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="col-span-full text-center py-10 text-[#5C736A] text-sm ek-card">
            Aucune cotisation créée.
          </div>
        )}
      </div>
    </div>
  );
}

/* =============== 6. PAIEMENTS (all) =============== */
function Paiements({ schoolId }) {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [cotisations, setCotisations] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", type: "mensualite", montant: 0, mode: "espece", mois: "octobre", cotisation_id: "", date: new Date().toISOString().slice(0, 10), reference: "" });

  const load = async () => {
    try {
      const [p, s, c] = await Promise.all([
        api.get("/payments"),
        api.get("/students"),
        api.get("/finance/cotisations"),
      ]);
      setPayments(p.data);
      setStudents([...s.data].sort((a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
        || (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" })
      ));
      setCotisations(c.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId]);

  const submit = async () => {
    try {
      await api.post("/payments", form);
      toast.success("Paiement enregistré");
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Supprimer ce paiement ?")) return;
    try { await api.delete(`/payments/${id}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return payments;
    return payments.filter((p) => {
      const st = students.find((x) => x.id === p.student_id);
      return st && `${st.prenom} ${st.nom} ${st.matricule}`.toLowerCase().includes(s);
    });
  }, [payments, students, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
          <Input className="pl-9" placeholder="Rechercher un élève…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button className="bg-[#1F7A63] hover:bg-[#155744] text-white" onClick={() => setOpen(true)} data-testid="pay-new">
            <Plus className="w-4 h-4 mr-1" /> Nouveau paiement
          </Button>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau paiement</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Élève</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} — {s.matricule}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES_PAIEMENT.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.type === "mensualite" && (
                <div>
                  <Label>Mois</Label>
                  <Select value={form.mois} onValueChange={(v) => setForm({ ...form, mois: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{MOIS.map((m) => <SelectItem key={m} value={m}>{MOIS_LBL[m]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.type === "cotisation" && (
                <div>
                  <Label>Cotisation</Label>
                  <Select value={form.cotisation_id} onValueChange={(v) => { const c = cotisations.find((x) => x.id === v); setForm({ ...form, cotisation_id: v, montant: c ? c.montant : form.montant }); }}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                    <SelectContent>{cotisations.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom} ({fmt(c.montant)} FCFA)</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Montant</Label><Input type="number" value={form.montant} onChange={(e) => setForm({ ...form, montant: Number(e.target.value) })} /></div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              </div>
              <div>
                <Label>Mode de paiement</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MODES_PAIEMENT.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Référence (opt.)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="N° transaction Wave, chèque…" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#155744] text-white">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="ek-card p-0 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-[#5C736A]">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Élève</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Mode</th>
              <th className="text-right px-3 py-2">Montant</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const st = students.find((s) => s.id === p.student_id);
              const tp = TYPES_PAIEMENT.find((t) => t.id === p.type);
              const md = MODES_PAIEMENT.find((m) => m.id === p.mode);
              return (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap">{p.date}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{st ? `${st.prenom} ${st.nom}` : "—"}</td>
                  <td className="px-3 py-2">{tp?.label || p.type}{p.mois ? ` (${MOIS_LBL[p.mois] || p.mois})` : ""}</td>
                  <td className="px-3 py-2">{md?.label || p.mode}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[#1F7A63]">{fmt(p.montant)}</td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    <a href={`${API_BASE}/payments/${p.id}/receipt.pdf`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="mr-1"><FileDown className="w-3 h-3 mr-1" /> Reçu</Button>
                    </a>
                    <Button size="sm" variant="ghost" onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#5C736A] text-sm">Aucun paiement.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =============== 7. DÉPENSES =============== */
function Depenses({ schoolId }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ montant: 0, categorie: "divers", description: "", date: new Date().toISOString().slice(0, 10), responsable: "" });

  const load = async () => {
    try { setList((await api.get("/expenses", { params: { school_id: schoolId } })).data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId]);

  const submit = async () => {
    try {
      await api.post("/expenses", { ...form, school_id: schoolId });
      toast.success("Dépense enregistrée");
      setOpen(false); setForm({ montant: 0, categorie: "divers", description: "", date: new Date().toISOString().slice(0, 10), responsable: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Supprimer cette dépense ?")) return;
    try { await api.delete(`/expenses/${id}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <Button className="bg-[#1F7A63] hover:bg-[#155744] text-white" onClick={() => setOpen(true)} data-testid="dep-new">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle dépense
          </Button>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle dépense</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Catégorie</Label>
                <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Montant</Label><Input type="number" value={form.montant} onChange={(e) => setForm({ ...form, montant: Number(e.target.value) })} /></div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              </div>
              <div><Label>Responsable</Label><Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#155744] text-white">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="ek-card p-0 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-[#5C736A]">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Catégorie</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">Responsable</th>
              <th className="text-right px-3 py-2">Montant</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((e) => {
              const cat = CATEGORIES.find((c) => c.id === e.categorie);
              return (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap">{e.date}</td>
                  <td className="px-3 py-2">{cat?.label || e.categorie}</td>
                  <td className="px-3 py-2 max-w-[300px] truncate">{e.description || e.motif}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{e.responsable || "—"}</td>
                  <td className="px-3 py-2 text-right font-semibold text-red-600">{fmt(e.montant)}</td>
                  <td className="px-3 py-2 text-center"><Button size="sm" variant="ghost" onClick={() => del(e.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#5C736A] text-sm">Aucune dépense.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =============== UI helpers =============== */
function Kpi({ label, value, suffix, icon: Icon, tone = "slate" }) {
  const tones = {
    green: "bg-[#E8F2EF] text-[#1F7A63]",
    yellow: "bg-[#FCF8E6] text-[#b38f00]",
    red: "bg-[#fbebeb] text-[#D93838]",
    blue: "bg-blue-50 text-blue-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="ek-card p-3 sm:p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs text-[#5C736A] uppercase truncate">{label}</div>
          <div className="mt-1 text-xl sm:text-2xl font-bold truncate" style={{ fontFamily: "Outfit" }}>{value}</div>
          {suffix && <div className="text-[10px] text-[#5C736A]">{suffix}</div>}
        </div>
        <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${tones[tone]}`}><Icon className="w-4 h-4" /></div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="ek-card p-4">
      <div className="text-sm font-semibold mb-3" style={{ fontFamily: "Outfit" }}>{title}</div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center text-xs text-[#5C736A] py-8">{text}</div>;
}
